import { useColorMode, useDark } from '@vueuse/core'
import {
  readonly,
  ref,
  shallowReactive,
  toRaw,
  watch,
  type DeepReadonly,
} from 'vue'
import { defineStore } from 'pinia'

import {
  CurrencyCode,
  PreferenceAppearanceThemes,
  PreferenceLanguages,
  Preferences,
  PreferencesUpdate,
  UserProfileService,
} from '@/client'
import { useSessionStore } from './sessionStore'

export const usePreferenceStore = defineStore('preference', () => {
  const sessionStore = useSessionStore()

  const defaultPreferences: Preferences = {
    language: PreferenceLanguages.EN,
    appearance: PreferenceAppearanceThemes.LIGHT_THEME,
    sidebar_collapse: false,
    show_onboarding_dialog: true,
    automatic_logout_time: -1,
    currency_code: CurrencyCode.EUR,
    date_format: 'YYYY-MM-DD',
  }

  const preferences = shallowReactive<Preferences>({ ...defaultPreferences })
  const preferenceKeys: (keyof Preferences)[] = [
    'language',
    'appearance',
    'sidebar_collapse',
    'show_onboarding_dialog',
    'automatic_logout_time',
    'currency_code',
    'date_format',
  ]
  const hasOwn = Object.prototype.hasOwnProperty
  const isHydrated = ref(false)
  const isLoading = ref(false)
  const lastUpdated = ref<Date | null>(null)
  const lastError = ref<unknown>(null)
  const hasHydratedFromCache = ref(false)

  let hydrationPromise: Promise<void> | null = null

  // ************************************************************
  // Color mode
  // ************************************************************
  // Update the underlying color mode whenever the stored appearance changes
  watch(
    () => preferences.appearance,
    () => {
      const colorMode = useColorMode()
      switch (preferences.appearance) {
        case PreferenceAppearanceThemes.DARK_THEME:
          colorMode.value = 'dark'
          break
        case PreferenceAppearanceThemes.LIGHT_THEME:
          colorMode.value = 'light'
          break
        default:
          colorMode.value = 'auto'
      }
    },
    { flush: 'post' }
  )

  // Toggle the appearance preference by flipping between light and dark themes
  async function toggleColorMode() {
    const isDark = useDark()
    switch (preferences.appearance) {
      case PreferenceAppearanceThemes.DARK_THEME:
        await updatePreferences({
          appearance: PreferenceAppearanceThemes.LIGHT_THEME,
        })
        break
      case PreferenceAppearanceThemes.LIGHT_THEME:
        await updatePreferences({
          appearance: PreferenceAppearanceThemes.DARK_THEME,
        })
        break
      default:
        // Get system color mode and toggle
        await updatePreferences({
          appearance: isDark.value
            ? PreferenceAppearanceThemes.LIGHT_THEME
            : PreferenceAppearanceThemes.DARK_THEME,
        })
    }
  }

  // ************************************************************
  // Get and update preferences
  // ************************************************************
  // Public helper to ensure preferences are hydrated before returning the snapshot
  async function getPreferences(force = false) {
    await ensureHydrated(force)
    return preferences as Preferences
  }

  // Make sure the store reflects the latest server state while deduplicating in-flight loads
  async function ensureHydrated(force = false) {
    const companyId = sessionStore.currentCompany?.company_id
    if (!companyId) {
      resetPreferencesToDefaults(true)
      return
    }

    if (!hasHydratedFromCache.value) {
      hydrateFromLocalStorage()
      hasHydratedFromCache.value = true
    }

    // Skip the fetch if we are already fresh and no force refresh was requested
    if (isHydrated.value && !force) return
    // If another request is already running, await its completion instead of firing again
    if (isLoading.value && hydrationPromise) {
      await hydrationPromise
      return
    }

    // Mark the hydration as in-progress and reset stale errors
    isLoading.value = true
    lastError.value = null

    hydrationPromise = (async () => {
      try {
        const userProfile = await UserProfileService.getUserProfileUserGet({})

        // Merge the server snapshot into the local store
        if (userProfile?.preferences) {
          applyPatch(userProfile.preferences)
        }

        // Mark the store as ready for consumers
        isHydrated.value = true
      } catch (error) {
        lastError.value = error
        throw error
      } finally {
        // Reset loading state and clear the promise once the run completes
        isLoading.value = false
        hydrationPromise = null
      }
    })()

    await hydrationPromise
  }

  // ************************************************************
  // Update preference
  // ************************************************************
  // Primary write path: accepts partials, applies optimistic updates, syncs with the API, rolls back on failure
  async function updatePreferences(
    partial: Partial<PreferencesUpdate>
  ): Promise<Preferences> {
    await ensureHydrated(false)

    if (!sessionStore.currentCompany?.company_id) {
      return preferences as Preferences
    }

    const previousValues = new Map<
      keyof Preferences,
      Preferences[keyof Preferences]
    >()
    const previousTimestamp = lastUpdated.value
    let hasChange = false
    const updates: Record<string, Preferences[keyof Preferences]> = {}

    const target = preferences as Record<
      keyof Preferences,
      Preferences[keyof Preferences]
    >

    for (const key of preferenceKeys) {
      if (!hasOwn.call(partial, key)) continue

      const next = partial[key]
      if (!isDefined(next)) continue

      const currentValue = target[key]
      previousValues.set(key, currentValue)
      const nextValue = next as Preferences[keyof Preferences]
      updates[key as string] = nextValue

      if (currentValue !== nextValue) {
        hasChange = true
      }
    }

    if (!hasChange) return preferences as Preferences

    applyPatch(updates as Partial<Preferences>)

    try {
      await UserProfileService.updateUserProfileUserPatch({
        requestBody: {
          preferences: updates as PreferencesUpdate,
        },
      })
      return preferences as Preferences
    } catch (error) {
      const revertPayload = Object.fromEntries(
        previousValues
      ) as Partial<Preferences>
      applyPatch(revertPayload, {
        persist: true,
        updateTimestamp: false,
      })
      lastUpdated.value = previousTimestamp
      lastError.value = error
      throw error
    }
  }

  return {
    preferences: readonly(preferences) as DeepReadonly<Preferences>,
    isHydrated,
    isLoading,
    getPreferences,
    ensureHydrated,
    updatePreferences,
    toggleColorMode,
    resetPreferencesToDefaults,
    lastUpdated,
    lastError,
  }

  // Apply a shallow merge into the store while handling persistence bookkeeping
  function applyPatch(
    partial: Partial<Preferences>,
    options: { persist?: boolean; updateTimestamp?: boolean } = {}
  ) {
    const { persist = true, updateTimestamp = true } = options
    let changed = false

    const target = preferences as Record<
      keyof Preferences,
      Preferences[keyof Preferences]
    >

    for (const key of preferenceKeys) {
      // Only touch keys that are present on the patch payload
      if (!hasOwn.call(partial, key)) continue

      const next = partial[key]
      // Ignore undefined/null assignments to avoid clobbering real values
      if (!isDefined(next)) continue

      // Skip if there is no effective change
      if (target[key] === next) continue

      // Perform the mutation on the shallow reactive target
      target[key] = next as Preferences[keyof Preferences]
      changed = true
    }

    // Bubble up an updated timestamp so consumers can react to fresh data
    if (changed && updateTimestamp) {
      lastUpdated.value = new Date()
    }

    // Persist the new snapshot for fast reloads unless explicitly disabled
    if (changed && persist) {
      persistPreferences()
    }

    return changed
  }

  // Serialize the current preference snapshot into local storage for reuse on reload
  function persistPreferences() {
    const snapshot = { ...toRaw(preferences) }
    localStorage.setItem('preferences', JSON.stringify(snapshot))
  }

  function clearPersistedPreferences() {
    localStorage.removeItem('preferences')
  }

  // Bootstrap the store from local storage to avoid rendering default values before hydration completes
  function hydrateFromLocalStorage() {
    try {
      const cachedRaw = localStorage.getItem('preferences')
      if (!cachedRaw) return
      const cached = JSON.parse(cachedRaw) as Partial<Preferences>
      if (cached && typeof cached === 'object') {
        applyPatch(cached, { persist: false, updateTimestamp: false })
      }
    } catch (error) {
      console.warn('Failed to hydrate preferences from cache', error)
    }
  }

  function resetPreferencesToDefaults(clearCache = false) {
    applyPatch(
      { ...defaultPreferences },
      { persist: false, updateTimestamp: false }
    )
    isHydrated.value = false
    isLoading.value = false
    lastError.value = null
    lastUpdated.value = null
    hydrationPromise = null
    hasHydratedFromCache.value = false

    if (clearCache) {
      clearPersistedPreferences()
    }
  }

  // Type guard that filters out nullish assignments
  function isDefined<T>(value: T | null | undefined): value is T {
    return value !== undefined && value !== null
  }
})
